import { TheNightlySection } from '@news-mono/common'
import { IconChevronUpTN, metrics } from '@news-mono/component-library'
import {
    Topic,
    AdTargeting,
    GetRouteAdTargeting,
    PageType, parsePageParam, getPaginatedCanonicalUrl,
} from '@news-mono/web-common'
import { layout } from '../../App.routing'
import H from 'history'
import React from 'react'
import { billboardLeaderboardMrec } from '../ad-units'
import queryString from 'query-string'

const MAIN_CONTENT_ID = 'main-content'

type createTheNightlyOnLandingPageArgs = {
    baseUrl: string
    topic: Topic
    section: TheNightlySection
    getAdTargeting: GetRouteAdTargeting
    location: H.Location<any>
}

export function createTheNightlyOnLandingPage({
    topic,
    section,
    getAdTargeting,
    baseUrl,
    location,
}: createTheNightlyOnLandingPageArgs): PageType<TheNightlySection> {
    const query = queryString.parse(location.search)
    const page = parsePageParam(query.page)
    const canonicalUrl =
        page > 1 ? getPaginatedCanonicalUrl(baseUrl, page) : baseUrl
    const title = `The Nightly On | The Nightly${
        page > 1 ? ` | Page ${page}` : ''
    }`

    const { adUnitPath, ssAdUnits, topics } = getAdTargeting(
        'home',
        section,
        topic,
    )
    const adTargeting: AdTargeting = {
        pageId: topic.title,
        adUnitPath,
        ssAdUnits,
        topics,
    }

    return {
        kind: 'page',
        heading: 'The Nightly On',
        pageType: 'topic',
        hideHeading: true,
        refreshOnInactive: false,
        pageMeta: {
            title,
            description:
                topic.seoDescription || `The latest in ${topic.seoTitle}`,
            meta: [
                {
                    property: 'og:description',
                    content:
                        topic.seoDescription ||
                        `The latest in ${topic.seoTitle}`,
                },
                {
                    property: 'og:title',
                    content: `${topic.seoTitle || topic.title} | The Nightly`,
                },
                {
                    property: 'twitter:description',
                    content:
                        topic.seoDescription ||
                        `The latest in ${topic.seoTitle}`,
                },
                {
                    property: 'og:image:width',
                    content: '1200',
                },
                {
                    property: 'og:image:height',
                    content: '630',
                },
            ],
            link: [{ rel: 'canonical', href: canonicalUrl }],
        },
        adTargeting,
        section,
        compositions: [
            layout.composition({
                type: 'thor',
                props: {
                    containerWidth: '100%',
                    horizontalGutters: false,
                    verticalGutters: false,
                },
                contentAreas: {
                    main: [
                        layout.nestedComposition({
                            type: 'box',
                            props: {
                                horizontalSpacingBreakpoint: 'sm',
                                horizontalSpacing: 'outerMargin',
                            },
                            contentAreas: {
                                main: [
                                    layout.nestedComposition({
                                        type: 'box',
                                        props: {
                                            verticalSpacing: 'md',
                                            containerWidth:
                                                metrics.thenightly.siteMetrics
                                                    .mainContentWidth,
                                        },
                                        contentAreas: {
                                            main: [
                                                layout.component({
                                                    type: 'nightly-special-edition',
                                                    props: {
                                                        dataDefinitionArgs: {
                                                            type: 'edition',
                                                            page: 1,
                                                            pageSize: 1,
                                                            publication:
                                                                'the_nightly_on',
                                                        },
                                                        lazyloadImages: true,
                                                        shouldShowAllEditions:
                                                            false,
                                                    },
                                                }),
                                            ],
                                        },
                                    }),
                                    layout.nestedComposition({
                                        type: 'box',
                                        props: {
                                            flex: {
                                                default: {
                                                    hasBeforeAfterMargins: true,
                                                    flexDirection: 'column',
                                                    gap: '40px',
                                                },
                                                lg: {
                                                    hasBeforeAfterMargins: true,
                                                    flexDirection: 'column',
                                                    gap: '80px',
                                                },
                                            },
                                        },
                                        contentAreas: {
                                            main: [
                                                ...billboardLeaderboardMrec(
                                                    'one',
                                                ),
                                            ],
                                        },
                                    }),
                                ],
                            },
                        }),
                        layout.nestedComposition({
                            type: 'box',
                            props: {
                                flex: {
                                    default: {
                                        justifyContent: 'center',
                                        flexDirection: 'column',
                                        gap: '40px',
                                    },
                                    lg: {
                                        gap: '80px',
                                    },
                                },
                                containerWidth: '100%',
                            },
                            contentAreas: {
                                main: [
                                    layout.nestedComposition({
                                        type: 'collection-data-context',
                                        props: {
                                            contextId: MAIN_CONTENT_ID,
                                            dataDefinitionArgs: {
                                                type: 'edition',
                                                page,
                                                throw404OnNoResults: true,
                                                pageSize: 4,
                                                publication: 'the_nightly_on',
                                            },
                                        },
                                        contentAreas: {
                                            children: [
                                                layout.component({
                                                    type: 'nightly-previous-special-editions',
                                                    props: {
                                                        dataDefinitionArgs: {
                                                            type: 'edition',
                                                            page,
                                                            pageSize: 4,
                                                            publication:
                                                                'the_nightly_on',
                                                        },
                                                    },
                                                }),
                                                layout.nestedComposition({
                                                    type: 'box',
                                                    props: {
                                                        horizontalSpacingBreakpoint:
                                                            'sm',
                                                        horizontalSpacing:
                                                            'outerMargin',
                                                        flex: {
                                                            default: {
                                                                flexDirection:
                                                                    'column',
                                                                gap: '40px',
                                                            },
                                                            lg: {
                                                                gap: '32px',
                                                            },
                                                        },
                                                    },
                                                    contentAreas: {
                                                        main: [
                                                            layout.component({
                                                                type: 'pagination',
                                                                props: {
                                                                    contextId:
                                                                        MAIN_CONTENT_ID,
                                                                },
                                                            }),
                                                            layout.component({
                                                                type: 'nightly-button',
                                                                props: {
                                                                    variant:
                                                                        'default',
                                                                    text: 'BACK TO TOP',
                                                                    color: 'primary',
                                                                    fill: 'text',
                                                                    layout: {
                                                                        alignSelf:
                                                                            'center',
                                                                    },
                                                                    action: {
                                                                        type: 'button',
                                                                        onClick:
                                                                            () =>
                                                                                window.scrollTo(
                                                                                    {
                                                                                        top: 0,
                                                                                    },
                                                                                ),
                                                                    },
                                                                    icon: {
                                                                        iconPosition:
                                                                            'right',
                                                                        IconElement:
                                                                            (
                                                                                <IconChevronUpTN />
                                                                            ),
                                                                    },
                                                                },
                                                            }),
                                                        ],
                                                    },
                                                }),
                                                ...billboardLeaderboardMrec(
                                                    'two',
                                                ),
                                            ],
                                        },
                                    }),
                                ],
                            },
                        }),
                    ],
                },
            }),
        ],
    }
}
